/**
 * Scheduling Hooks
 * Custom React hooks for scheduling functionality
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { schedulingApi } from '@/lib/api/services/scheduling';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import React from 'react';

// ============================================================================
// EVENT TYPES
// ============================================================================

/**
 * Hook to fetch all event types
 */
export function useEventTypes(userId?: string) {
    return useQuery({
        queryKey: ['event-types', userId],
        queryFn: () => schedulingApi.getEventTypes(userId),
        staleTime: 30 * 1000, // 30 seconds
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    });
}

/**
 * Hook to fetch a single event type by ID
 */
export function useEventType(id: string) {
    return useQuery({
        queryKey: ['event-type', id],
        queryFn: () => schedulingApi.getEventTypeById(id),
        enabled: !!id,
    });
}

/**
 * Hook to fetch event type by slug (public)
 */
export function useEventTypeBySlug(slug: string) {
    return useQuery({
        queryKey: ['event-type-slug', slug],
        queryFn: () => schedulingApi.getEventTypeBySlug(slug),
        enabled: !!slug,
    });
}

/**
 * Hook to create a new event type
 */
export function useCreateEventType() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: schedulingApi.createEventType,
        onSuccess: (res: any, variables: any) => {
            // apiClient swallows HTTP errors into { success:false } — surface them.
            if (res?.success === false) {
                toast({ title: res?.message || res?.error || 'Failed to create event type', variant: "destructive" });
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['event-types'] });
            
            const name = variables?.name?.trim();
            const slug = res?.data?.slug || res?.slug || res?.data?.id || res?.id;
            
            // Build a relative URL so it works seamlessly if deployed on subpaths, but usually origin + /book is fine
            const eventLink = slug ? `${window.location.origin}/book/${slug}` : '';

            toast({
                variant: "success",
                title: "Event type created successfully!",
                description: name ? `Your booking page for "${name}" is live and ready to be shared.` : 'Your new booking page is live and ready to be shared.',
                action: eventLink ? React.createElement(
                    ToastAction,
                    { 
                        altText: "Copy Link",
                        onClick: () => {
                            navigator.clipboard.writeText(eventLink);
                            toast({ title: "Link copied to clipboard!", variant: "success" });
                        }
                    },
                    "Copy Link"
                ) : undefined,
            });
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to create event type', variant: "destructive" });
        },
    });
}

/**
 * Hook to update an event type
 */
export function useUpdateEventType() {
    const queryClient = useQueryClient();

    return useMutation({
        // `silent: true` skips the hook's success toast — callers that show
        // their own copy (e.g. pause/resume on the Scheduling page) use it.
        mutationFn: ({ id, data }: { id: string; data: any; silent?: boolean }) =>
            schedulingApi.updateEventType(id, data),
        onSuccess: (res: any, variables) => {
            // apiClient swallows HTTP errors into { success:false } — surface them.
            if (res?.success === false) {
                toast({ title: res?.message || res?.error || 'Failed to update event type', variant: "destructive" });
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['event-types'] });
            queryClient.invalidateQueries({ queryKey: ['event-type', variables.id] });
            if (variables.silent) return;
            const name = typeof variables.data?.name === 'string' ? variables.data.name.trim() : '';
            toast({
                variant: "success",
                title: name ? `"${name}" updated` : 'Event type updated',
                description: 'Your changes are live on the booking page.',
            });
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to update event type', variant: "destructive" });
        },
    });
}

/**
 * Hook to delete an event type
 */
export function useDeleteEventType() {
    const queryClient = useQueryClient();

    return useMutation({
        // Accepts either an id string (legacy callers) or { id, force }.
        mutationFn: (input: string | { id: string; force?: boolean }) => {
            if (typeof input === 'string') return schedulingApi.deleteEventType(input);
            return input.force
                ? schedulingApi.deleteEventType(input.id, { force: true })
                : schedulingApi.deleteEventType(input.id);
        },
        onSuccess: (res: any) => {
            // apiClient swallows HTTP errors into { success:false, ... } — a 409
            // "has active bookings" response lands here, NOT in onError. The
            // caller branches on res.success; don't invalidate on failure.
            if (res?.success === false) return;
            queryClient.invalidateQueries({ queryKey: ['event-types'] });
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
            // Success toast is owned by the caller (Scheduling.tsx), which knows
            // the event name + how many bookings were cancelled — richer copy
            // than a generic "deleted successfully" from here.
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to delete event type', variant: "destructive" });
        },
    });
}

// ============================================================================
// BOOKINGS
// ============================================================================

/**
 * Hook to fetch all bookings
 */
export function useBookings(eventTypeId?: string) {
    return useQuery({
        queryKey: ['bookings', eventTypeId],
        queryFn: () => schedulingApi.getBookings(eventTypeId),
        staleTime: 30 * 1000, // 30 seconds
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    });
}

export function useBookingsPaged(params: {
    eventTypeId?: string;
    limit?: number;
    offset?: number;
    timeFilter?: 'upcoming' | 'past' | 'all';
    status?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    enabled?: boolean;
} = {}) {
    const {
        eventTypeId,
        limit = 20,
        offset = 0,
        timeFilter = 'all',
        status,
        search,
        dateFrom,
        dateTo,
        enabled = true,
    } = params;

    return useQuery({
        queryKey: ['bookings', 'paged', eventTypeId || '', limit, offset, timeFilter, status || '', search || '', dateFrom || '', dateTo || ''],
        queryFn: () => schedulingApi.getBookingsPaged({ eventTypeId, limit, offset, timeFilter, status, search, dateFrom, dateTo }),
        staleTime: 30 * 1000,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        enabled,
        select: (res: any) => {
            const bookings = Array.isArray(res?.data) ? res.data : [];
            const pagination = res?.pagination || null;
            return { bookings, pagination };
        }
    });
}

/**
 * Hook to fetch scheduling contacts (unique invitees with last meeting date)
 */
export function useSchedulingContacts(params: {
    limit?: number;
    offset?: number;
    search?: string;
    enabled?: boolean;
} = {}) {
    const { limit = 20, offset = 0, search, enabled = true } = params;
    return useQuery({
        queryKey: ['scheduling-contacts', limit, offset, search || ''],
        queryFn: () => schedulingApi.getSchedulingContacts({ limit, offset, search }),
        staleTime: 30 * 1000,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        enabled,
        select: (res: any) => {
            const contacts = Array.isArray(res?.data) ? res.data : [];
            const pagination = res?.pagination || null;
            return { contacts, pagination };
        }
    });
}

/**
 * Hook to fetch a single booking by ID
 */
export function useBooking(id: string) {
    return useQuery({
        queryKey: ['booking', id],
        queryFn: () => schedulingApi.getBookingById(id),
        enabled: !!id,
    });
}

/**
 * Hook to create a new booking
 */
export function useCreateBooking() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: schedulingApi.createBooking,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
            toast({ title: 'Booking created successfully', variant: "success" });
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to create booking', variant: "destructive" });
        },
    });
}

/**
 * Hook to cancel a booking
 */
export function useCancelBooking() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, cancellation_reason }: { id: string; cancellation_reason?: string }) =>
            schedulingApi.cancelBooking(id, cancellation_reason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
            toast({ title: 'Booking cancelled successfully', variant: "success" });
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to cancel booking', variant: "destructive" });
        },
    });
}

/**
 * Hook to reschedule a booking
 */
export function useRescheduleBooking() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: { start_time: string; end_time: string } }) =>
            schedulingApi.rescheduleBooking(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
            toast({ title: 'Booking rescheduled successfully', variant: "success" });
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to reschedule booking', variant: "destructive" });
        },
    });
}

// ============================================================================
// AVAILABILITY
// ============================================================================

/**
 * Hook to fetch availability settings
 */
export function useAvailabilitySettings(userId?: string, eventTypeId?: string) {
    return useQuery({
        queryKey: ['availability-settings', userId, eventTypeId],
        queryFn: () => schedulingApi.getDefaultAvailability(userId),
        enabled: !!userId,
        staleTime: 60 * 1000, // 1 minute
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    });
}

/**
 * Hook to create availability settings
 */
export function useCreateAvailabilitySettings() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: schedulingApi.createAvailabilitySetting,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['availability-settings'] });
            toast({ title: 'Availability settings created successfully', variant: "success" });
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to create availability settings', variant: "destructive" });
        },
    });
}

/**
 * Hook to update availability settings
 */
export function useUpdateAvailabilitySettings() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) =>
            schedulingApi.updateAvailabilitySetting(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['availability-settings'] });
            toast({ title: 'Availability settings updated successfully', variant: "success" });
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to update availability settings', variant: "destructive" });
        },
    });
}

/**
 * Hook to fetch available time slots
 */
export function useAvailableSlots(eventTypeId: string, startDate: string, endDate: string) {
    return useQuery({
        queryKey: ['available-slots', eventTypeId, startDate, endDate],
        queryFn: () => schedulingApi.getAvailableSlots(eventTypeId, startDate, endDate),
        enabled: !!eventTypeId && !!startDate && !!endDate,
        staleTime: 60 * 1000, // 1 minute
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    });
}

/**
 * Hook to fetch available time slots for a date range
 */
export function useAvailableSlotsRange(eventTypeId: string, startDate: string, endDate: string, enabled = true) {
    return useQuery({
        queryKey: ['available-slots-range', eventTypeId, startDate, endDate],
        queryFn: () => schedulingApi.getAvailableSlotsRange(eventTypeId, startDate, endDate),
        enabled: !!eventTypeId && !!startDate && !!endDate && enabled,
        staleTime: 60 * 1000, // 1 minute
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        select: (res: any) => res?.data || {},
    });
}

/**
 * Hook to fetch all scheduling users (potential hosts)
 */
export function useSchedulingUsers() {
    return useQuery({
        queryKey: ['scheduling-users'],
        queryFn: () => schedulingApi.getSchedulingUsers(),
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    });
}

/**
 * Whether the org has a payment gateway connected to collect paid webinar
 * registration fees. Used to warn the host before creating a paid event.
 * `enabled` lets callers skip the fetch until it's actually needed (webinar kind).
 */
export function useSchedulingPaymentGatewayStatus(enabled = true) {
    return useQuery({
        queryKey: ['scheduling-payment-gateway-status'],
        queryFn: () => schedulingApi.getPaymentGatewayStatus(),
        enabled,
        // 30s TTL mirrors the connection-check cache convention (commit #1177) —
        // short enough that connecting a gateway is reflected on the next open,
        // long enough that concurrent/re-mounts don't re-hit the network.
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
    });
}

/**
 * Hook to fetch current user's profile and organization info
 */
export function useCurrentSchedulingUser() {
    return useQuery({
        queryKey: ['scheduling-current-user'],
        queryFn: () => schedulingApi.getCurrentUser(),
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    });
}
